import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, Link2, Users, Copy, Eye, Trash2, ArrowLeft, CheckCircle, FileText, PlayCircle } from 'lucide-react';
import { briefingService, Briefing } from '../../../services/briefingService';
import { TIPOS_PROJETO, TipoProjeto } from '../../../config/briefingConfig';
import api from '../../../services/api';

type View = 'list' | 'detail';

const statusConfig: Record<string, { label: string; className: string }> = {
  PENDENTE: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  RESPONDIDO: { label: 'Respondido', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  CONCLUIDO: { label: 'Concluído', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
};

const tipoLabel = (tipo?: string) => TIPOS_PROJETO.find((t) => t.id === tipo)?.label || tipo || '—';

interface Cliente {
  id: string;
  razaoSocial: string;
  nomeFantasia: string | null;
}

export function BriefingManagement() {
  const navigate = useNavigate();
  const [view, setView] = useState<View>('list');
  const [briefings, setBriefings] = useState<Briefing[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Briefing | null>(null);

  // Modal de novo pedido
  const [modalAberto, setModalAberto] = useState(false);
  const [modo, setModo] = useState<'link' | 'cliente'>('link');
  const [tipoSelecionado, setTipoSelecionado] = useState<TipoProjeto>('SAAS_PRAGAS');
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [clienteSelecionado, setClienteSelecionado] = useState('');
  const [linkGerado, setLinkGerado] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [copiado, setCopiado] = useState(false);

  // Devolução para revisão
  const [devolverAberto, setDevolverAberto] = useState(false);
  const [mensagemRevisao, setMensagemRevisao] = useState('');
  const [devolvendo, setDevolvendo] = useState(false);

  // Conversão para atendimento
  const [convertendo, setConvertendo] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    loadBriefings();
  }, []);

  const loadBriefings = async () => {
    try {
      setLoading(true);
      const data = await briefingService.list();
      setBriefings(data);
    } catch (error) {
      console.error('Erro ao carregar briefings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadClientes = async () => {
    try {
      const response = await api.get('/admin/clients');
      setClientes(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const openModal = () => {
    setModalAberto(true);
    setModo('link');
    setTipoSelecionado('SAAS_PRAGAS');
    setLinkGerado(null);
    setClienteSelecionado('');
    setCopiado(false);
    loadClientes();
  };

  const handleCriar = async () => {
    setSalvando(true);
    try {
      if (modo === 'link') {
        const briefing = await briefingService.create({ tipo: tipoSelecionado });
        setLinkGerado(`${window.location.origin}/public/briefing/${briefing.tokenPublico}`);
        await loadBriefings();
      } else {
        if (!clienteSelecionado) return;
        await briefingService.create({ clienteId: clienteSelecionado, tipo: tipoSelecionado });
        setModalAberto(false);
        await loadBriefings();
      }
    } catch (error) {
      console.error('Erro ao criar briefing:', error);
      alert('Erro ao criar briefing');
    } finally {
      setSalvando(false);
    }
  };

  const handleCopy = async () => {
    if (!linkGerado) return;
    try {
      // navigator.clipboard só existe em contexto seguro (HTTPS/localhost);
      // fallback via execCommand para HTTP (ex: folio-dev.local).
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(linkGerado);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = linkGerado;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar link:', error);
    }
  };

  const handleOpenDetail = (b: Briefing) => {
    setSelected(b);
    setView('detail');
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este briefing?')) return;
    try {
      await briefingService.delete(id);
      await loadBriefings();
    } catch (error) {
      console.error('Erro ao excluir briefing:', error);
      alert('Erro ao excluir briefing');
    }
  };

  const handleConcluir = async (id: string) => {
    try {
      await briefingService.update(id, { status: 'CONCLUIDO' });
      await loadBriefings();
      if (selected?.id === id) {
        setSelected({ ...selected, status: 'CONCLUIDO' });
      }
    } catch (error) {
      console.error('Erro ao concluir briefing:', error);
    }
  };

  const handleDevolver = async () => {
    if (!selected) return;
    setDevolvendo(true);
    try {
      const b = await briefingService.devolver(selected.id, mensagemRevisao);
      setSelected(b);
      setDevolverAberto(false);
      setMensagemRevisao('');
      await loadBriefings();
    } catch (error: any) {
      console.error('Erro ao devolver briefing:', error);
      alert(error.response?.data?.error || 'Erro ao devolver briefing');
    } finally {
      setDevolvendo(false);
    }
  };

  const handleConverter = async () => {
    if (!selected) return;
    setConvertendo(true);
    try {
      const ticket = selected.clienteId
        ? await briefingService.converterCliente(selected.id)
        : await briefingService.converterProspect(selected.id);

      setToast('Atendimento iniciado com sucesso!');
      setTimeout(() => {
        navigate(`/dashboard?atendimento=${ticket.id}`);
      }, 800);
    } catch (error: any) {
      console.error('Erro ao iniciar atendimento:', error);
      setToast(error.response?.data?.error || 'Erro ao iniciar atendimento');
    } finally {
      setConvertendo(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (view === 'detail' && selected) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={() => { setView('list'); setSelected(null); }} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-foreground">
              {selected.cliente?.nomeFantasia || selected.cliente?.razaoSocial || selected.nomeEmpresa || 'Briefing'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {selected.email || selected.cliente?.razaoSocial || ''} · Criado em {formatDate(selected.createdAt)}
            </p>
          </div>
          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-muted/50 text-foreground">
            {tipoLabel(selected.tipo)}
          </span>
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusConfig[selected.status]?.className}`}>
            {statusConfig[selected.status]?.label}
          </span>
        </div>

        {/* Dados formatados */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Respostas do Briefing</h2>
          {!selected.dados || Object.keys(selected.dados).length === 0 ? (
            <p className="text-muted-foreground">Este briefing ainda não foi respondido.</p>
          ) : (
            <div className="space-y-6">
              {Object.entries(selected.dados).map(([chave, valor]) => {
                if (chave === '_anexos') return null;
                if (Array.isArray(valor)) {
                  if (valor.length === 0) return null;
                  return (
                    <div key={chave}>
                      <h3 className="text-sm font-medium text-muted-foreground mb-2">{formatKey(chave)}</h3>
                      <div className="flex flex-wrap gap-2">
                        {valor.map((item) => (
                          <span key={String(item)} className="px-2 py-1 bg-muted/50 rounded-md text-sm text-foreground">
                            {String(item)}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                }
                if (typeof valor === 'string' && !valor.trim()) return null;
                return (
                  <div key={chave}>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{formatKey(chave)}</h3>
                    <p className="text-foreground whitespace-pre-wrap">{String(valor)}</p>
                  </div>
                );
              })}
              {selected.dados?._anexos && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Anexos</h3>
                  <ul className="space-y-1">
                    {Object.entries(selected.dados._anexos as Record<string, string>).map(([chave, nome]) => (
                      <li key={chave} className="flex items-center gap-2 text-sm text-foreground">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">{formatKey(chave)}:</span> {nome}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {selected.mensagemRevisao && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-1">
              Mensagem enviada ao cliente (revisão)
            </h3>
            <p className="text-sm text-foreground whitespace-pre-wrap">{selected.mensagemRevisao}</p>
          </div>
        )}

        {selected.status === 'RESPONDIDO' && (
          <div className="bg-card border border-brand/30 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Ações</h3>
            <button
              onClick={handleConverter}
              disabled={convertendo}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {convertendo ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <PlayCircle className="w-4 h-4" />
              )}
              {selected.clienteId
                ? 'Iniciar Atendimento com Briefing Anexo'
                : 'Cadastrar Cliente e Iniciar Atendimento'}
            </button>
            <p className="text-xs text-muted-foreground">
              {selected.clienteId
                ? 'Cria um ticket de atendimento para este cliente com o briefing anexo.'
                : 'Cadastra o prospect como cliente e cria o ticket de atendimento.'}
            </p>
          </div>
        )}

        {selected.status !== 'CONCLUIDO' && (
          <div className="flex items-center gap-3">
            {selected.status === 'RESPONDIDO' && (
              <button
                onClick={() => { setDevolverAberto(true); setMensagemRevisao(''); }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Devolver para Revisão
              </button>
            )}
            <button
              onClick={() => handleConcluir(selected.id)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <CheckCircle className="w-4 h-4" />
              Marcar como Concluído
            </button>
          </div>
        )}

        {/* Modal de devolução para revisão */}
        {devolverAberto && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-lg w-full max-w-lg p-6 space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Devolver para Revisão</h3>
              <p className="text-sm text-muted-foreground">
                Informe o que o cliente precisa revisar ou complementar. O briefing voltará para o status Pendente.
              </p>
              <textarea
                value={mensagemRevisao}
                onChange={(e) => setMensagemRevisao(e.target.value)}
                placeholder="Ex: Favor detalhar melhor os serviços prestados e informar o prazo desejado..."
                rows={5}
                className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-brand resize-none"
              />
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setDevolverAberto(false)}
                  className="flex-1 px-4 py-2 bg-muted/50 text-muted-foreground rounded-lg hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDevolver}
                  disabled={devolvendo || mensagemRevisao.trim().length < 5}
                  className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {devolvendo ? 'Enviando...' : 'Devolver'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[60] px-4 py-3 bg-foreground text-background rounded-lg shadow-lg text-sm font-medium">
            {toast}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Briefings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gerencie pedidos de briefing de novos projetos
          </p>
        </div>
        <button
          onClick={openModal}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white rounded-[8px] shadow-[0_4px_12px_-2px_rgba(37,99,235,0.35)] hover:shadow-[0_8px_18px_-4px_rgba(37,99,235,0.45)] hover:-translate-y-0.5 transition-all duration-200"
        >
          <Plus className="w-4 h-4" />
          Novo Pedido de Briefing
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-brand" />
        </div>
      ) : briefings.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-lg">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Nenhum briefing cadastrado</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Data</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Empresa / Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {briefings.map((b) => (
                  <tr key={b.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatDate(b.createdAt)}</td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-foreground font-medium">
                        {b.cliente?.nomeFantasia || b.cliente?.razaoSocial || b.nomeEmpresa || '—'}
                      </div>
                      <div className="text-xs text-muted-foreground">{b.email || b.contato || ''}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground">{tipoLabel(b.tipo)}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusConfig[b.status]?.className}`}>
                        {statusConfig[b.status]?.label}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenDetail(b)}
                          className="text-brand hover:text-brand/80 transition-colors p-1 rounded hover:bg-brand/10"
                          title="Visualizar"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(b.id)}
                          className="text-red-600 hover:text-red-800 transition-colors p-1 rounded hover:bg-red-100"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Novo Pedido */}
      {modalAberto && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg w-full max-w-md p-6 space-y-5">
            <h3 className="text-lg font-semibold text-foreground">Novo Pedido de Briefing</h3>

            {!linkGerado ? (
              <>
                {/* Tipo de projeto */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-foreground">Tipo de Projeto *</label>
                  <div className="space-y-2">
                    {TIPOS_PROJETO.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setTipoSelecionado(t.id)}
                        className={`w-full p-3 rounded-lg border text-left transition-colors ${
                          tipoSelecionado === t.id ? 'border-brand bg-brand/10' : 'border-border hover:border-brand/50'
                        }`}
                      >
                        <span className="block text-sm font-semibold text-foreground">{t.label}</span>
                        <span className="block text-xs text-muted-foreground mt-0.5">{t.descricao}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setModo('link')}
                    className={`p-4 rounded-lg border text-left transition-colors ${
                      modo === 'link' ? 'border-brand bg-brand/10' : 'border-border hover:border-brand/50'
                    }`}
                  >
                    <Link2 className="w-5 h-5 text-brand mb-2" />
                    <span className="block text-sm font-semibold text-foreground">Gerar Link Público</span>
                    <span className="block text-xs text-muted-foreground mt-1">Para prospects sem cadastro</span>
                  </button>
                  <button
                    onClick={() => setModo('cliente')}
                    className={`p-4 rounded-lg border text-left transition-colors ${
                      modo === 'cliente' ? 'border-brand bg-brand/10' : 'border-border hover:border-brand/50'
                    }`}
                  >
                    <Users className="w-5 h-5 text-brand mb-2" />
                    <span className="block text-sm font-semibold text-foreground">Enviar para Cliente</span>
                    <span className="block text-xs text-muted-foreground mt-1">Para base ativa cadastrada</span>
                  </button>
                </div>

                {modo === 'cliente' && (
                  <select
                    value={clienteSelecionado}
                    onChange={(e) => setClienteSelecionado(e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-brand"
                  >
                    <option value="">Selecione um cliente</option>
                    {clientes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nomeFantasia || c.razaoSocial}
                      </option>
                    ))}
                  </select>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setModalAberto(false)}
                    className="flex-1 px-4 py-2 bg-muted/50 text-muted-foreground rounded-lg hover:bg-muted transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCriar}
                    disabled={salvando || (modo === 'cliente' && !clienteSelecionado)}
                    className="flex-1 px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {salvando ? 'Criando...' : modo === 'link' ? 'Gerar Link' : 'Enviar'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Link público gerado com sucesso. Copie e compartilhe com o prospect:
                </p>
                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                  <input
                    readOnly
                    value={linkGerado}
                    className="flex-1 bg-transparent text-sm text-foreground focus:outline-none"
                  />
                  <button
                    onClick={handleCopy}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand text-white rounded-md text-sm hover:bg-brand/90 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    {copiado ? 'Copiado!' : 'Copiar'}
                  </button>
                </div>
                <button
                  onClick={() => setModalAberto(false)}
                  className="w-full px-4 py-2 bg-muted/50 text-muted-foreground rounded-lg hover:bg-muted transition-colors"
                >
                  Fechar
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatKey(chave: string): string {
  const map: Record<string, string> = {
    razaoSocial: 'Razão Social',
    nomeFantasia: 'Nome Fantasia',
    cnpj: 'CNPJ',
    telefone: 'Telefone / WhatsApp',
    email: 'E-mail Comercial',
    servicosPrestados: 'Serviços Prestados',
    pragasAlvo: 'Pragas-alvo',
    tipoCobranca: 'Tipo de Cobrança',
    criteriosPreco: 'Critérios de Precificação',
    descontosContratos: 'Descontos para Contratos',
    garantia: 'Garantia',
    frequencia: 'Frequência de Aplicação',
    vistoriasRetorno: 'Vistorias de Retorno',
    formasPagamento: 'Formas de Pagamento',
    parcelamento: 'Parcelamento',
    descontoAvista: 'Desconto à Vista',
    prazoValidade: 'Prazo de Validade do Orçamento',
    multaCancelamento: 'Multa / Taxa de Cancelamento',
    camposObrigatorios: 'Campos Obrigatórios (Cadastro)',
    tipoEstabelecimento: 'Tipos de Estabelecimento',
    camposComplementares: 'Campos Complementares',
    origemContato: 'Origem do Contato',
    etapasProcesso: 'Etapas do Processo Comercial',
    motivosPerda: 'Motivos de Perda',
    visitaTecnica: 'Visita Técnica',
    informacoesOs: 'Informações da O.S.',
    fluxoOs: 'Fluxo da O.S.',
    cargos: 'Cargos / Funções',
    regrasNegocio: 'Regras de Negócio',
    areaAtendimento: 'Área de Atendimento',
    horarios: 'Horários de Atendimento',
    equipe: 'Equipe e Veículos',
    sistemasAtuais: 'Sistemas Atuais',
    prioridadeAlta: 'Prioridade Alta',
    observacoes: 'Observações',
    proposta: 'Proposta Comercial',
    contrato: 'Contrato',
    planilhaClientes: 'Planilha de Clientes',
    tabelaPrecos: 'Tabela de Preços',
    ordemServico: 'Ordem de Serviço',
    alvara: 'Alvará / Licenças',
  };
  return map[chave] || (/^outrasInfo\d+$/.test(chave) ? 'Outras informações' : chave);
}
